import * as Sentry from "@sentry/browser";
import { Alert, Button, Col, message, Row, Spin } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import {
  GET_FIXED_INCOME_REQUEST_FOR_EDIT,
  UPDATE_FIXED_INCOME_REQUEST,
} from "../gql/fixed-income-requests";
import { SEARCH_CUSTOMER } from "../gql/customers";
import { GET_PROVIDERS } from "../gql/loans";
import { USERS_BY_ROLE_QUERY } from "../gql/users";
import {
  Customer,
  FixedIncomeRequest,
  FormFieldConfig,
  SelectOptionsInterface,
  User,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import { DEBOUNCE_TIME } from "../helpers/constants";

interface State {
  isLoading: boolean;
  id: string | number;
  fixedIncomeRequest?: FixedIncomeRequest;
  customers: Customer[];
  searchTerm: string;
  isSearching: boolean;
  selectedCustomer?: Customer;
  providers: SelectOptionsInterface[];
  products: SelectOptionsInterface[];
  selectedProviderId?: string;
  selectedProvider?: any;
  rawProviders: any[];
  showCancelModal: boolean;
  updateRequestError?: string;
  selectedProductId?: string;
  users: User[];
  selectedAssignedToId?: string;
  userSearchTerm: string;
}

interface RouteParams {
  id?: string;
}

export interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
  user: User;
  customer?: Customer;
  handleSuccess?: () => void;
}

const fieldsToBeValidated = [
  "customerId",
  "providerId",
  "productId",
  "assignedToId",
  "issuedDate",
];

class EditFixedIncomeRequest extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      fixedIncomeRequest: undefined,
      id: "",
      customers: [],
      searchTerm: "",
      isSearching: false,
      selectedCustomer: undefined,
      providers: [],
      products: [],
      selectedProviderId: undefined,
      rawProviders: [],
      showCancelModal: false,
      selectedProductId: undefined,
      users: [],
      selectedAssignedToId: undefined,
      userSearchTerm: "",
    };

    this.reference = React.createRef<FormInstance>();
    this.cancelRequest = this.cancelRequest.bind(this);
  }

  componentDidMount(): void {
    this.setState(
      { id: this.props.match.params.id || 0, isLoading: true },
      (): void => {
        Promise.all([this.getAllProviders(), this.getAdvisors()]).then(() => {
          this.getFixedIncomeRequest();
        });
      },
    );
  }

  getFixedIncomeRequest(): void {
    const { id } = this.state;
    interface Response {
      fixedIncomeRequest: FixedIncomeRequest;
    }
    const query = GET_FIXED_INCOME_REQUEST_FOR_EDIT;
    this.setState({ isLoading: true });
    request({
      query,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<{ data: Response; }>): void => {
        const fixedIncomeRequest = data?.data?.fixedIncomeRequest;
        const selectedCustomer = fixedIncomeRequest?.customer;

        this.setState(
          {
            fixedIncomeRequest,
            selectedCustomer,
            selectedProviderId: fixedIncomeRequest?.provider?.id,
            selectedProductId: fixedIncomeRequest?.product?.id,
            selectedAssignedToId: fixedIncomeRequest?.assignedTo?.id,
          },
          () => {
            this.handleProviderSelect(fixedIncomeRequest?.provider?.id || "");
            this.setFormValues(fixedIncomeRequest);
          },
        );
      })
      .catch((error) => {
        message.error("Failed to load fixed income request");
        Sentry.captureException(error);
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  }

  setFormValues = (fixedIncomeRequest: FixedIncomeRequest): void => {
    if (!this.reference.current || !fixedIncomeRequest) return;

    this.reference.current.setFieldsValue({
      customerId: fixedIncomeRequest.customer?.id,
      providerId: fixedIncomeRequest.provider?.id,
      productId: fixedIncomeRequest.product?.id,
      assignedToId: fixedIncomeRequest.assignedTo?.id,
    });
  };

  getAllProviders(): Promise<void> {
    return request({
      query: GET_PROVIDERS,
      variables: {
        page: 1,
        perPage: "100",
        productCategory: "fixed_income",
      },
    })
      .then(({ data: { data } }: any): void => {
        const allProviders = data.getProviders || [];

        const providers = allProviders.filter(
          (provider: any) => provider.name === "PSL",
        );

        this.setState({
          providers,
          rawProviders: providers,
        });
      })
      .catch((error) => {
        message.error("Failed to load providers");
        Sentry.captureException(error);
      });
  }

  getAdvisors = (): void => {
    request({
      query: USERS_BY_ROLE_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
        role: "Advisors",
      },
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          users: data.users.results,
        });
      })
      .catch((error) => {
        message.error("Failed to load users");
        Sentry.captureException(error);
      });
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAdvisors();
  }, DEBOUNCE_TIME);

  redirectToViewFixedIncomeRequestPage = (id: string): void => {
    this.props.history.push(`/fixed-income-requests/${id}`);
  };

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ searchTerm }, async () => {
      await this.triggerDelayedSearch();
    });
  };

  handleCustomerSelect = async (id: string | number): Promise<void> => {
    const customerId = String(id);
    const selectedCustomer = this.state.customers.find(
      (customer: Customer): boolean => customer.id === customerId,
    );

    this.setState({
      selectedCustomer,
    });
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    await this.triggerCustomerSearch();
  }, DEBOUNCE_TIME);

  triggerCustomerSearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1, numberPerPage: 50 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customers: mutation.results,
        });
      }
    } catch (error) {
      message.error(`customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  updateFixedIncomeRequest = async (): Promise<void> => {
    if (!this.reference.current) return;

    let data;

    const shouldRedirect = !this.props.customer;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error as any);
      return;
    }

    this.setState({ isLoading: true, updateRequestError: undefined });

    let variables = {
      id: this.state.fixedIncomeRequest?.id,
      customerId: data.customerId,
      providerId: data.providerId,
      productId: data.productId,
      assignedToId: data.assignedToId,
      source: "phoenix",
      issuedDate: data.issuedDate,
    };

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_FIXED_INCOME_REQUEST,
        variables,
      });

      const { err, mutation } = responseHandler<any>(
        response,
        "updateFixedIncomeRequest",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current?.setFields(err.map(mapAPIErrorToFormError));
        this.setState({
          updateRequestError: generateDisplayErrorMessage(err),
        });
      } else if (mutation?.fixedIncomeRequest) {
        message.success("Fixed income request updated successfully");
        if (shouldRedirect) {
          this.redirectToViewFixedIncomeRequestPage(
            mutation.fixedIncomeRequest.id,
          );
        }
        this.props.handleSuccess && this.props.handleSuccess();
      } else {
        message.error(`An unexpected error occurred - ${generateDisplayErrorMessage(err)}`);
        this.setState({ updateRequestError: "An unexpected error occurred" });
      }
    } catch (error) {
      message.error(`Failed to update fixed income request - ${generateDisplayErrorMessage(error)}`);
      this.setState({
        updateRequestError: "Failed to update fixed income request",
      });
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleCancelModal = async () => {
    this.setState({ showCancelModal: !this.state.showCancelModal });
  };

  cancelRequest = async () => {
    this.props.history.goBack();
  };

  handleProviderSelect = (id: string | number): void => {
    const providerId = String(id);
    const { rawProviders } = this.state;
    const selectedProvider = rawProviders.find((p) => p.id === providerId);

    if (!selectedProvider) {
      console.warn("Provider not found:", providerId);
      return;
    }

    const products = selectedProvider?.activeProducts || [];

    this.setState({
      selectedProviderId: providerId,
      selectedProvider,
      products,
    });
  };

  handleProductSelect = (id: string | number): void => {
    this.setState({
      selectedProductId: String(id),
    });
  };

  handleAssignedToSelect = (id: string | number): void => {
    this.setState({
      selectedAssignedToId: String(id),
    });
  };

  render(): JSX.Element {
    const {
      isLoading,
      fixedIncomeRequest,
      isSearching,
      customers,
      providers,
      products,
      showCancelModal,
      updateRequestError,
      users,
    } = this.state;

    const fields: FormFieldConfig[] = [
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder:
          "Search by Email, Phone, SSNIT, First name, Middle name, Last name or PetraID ...",
        rule: "string",
        label: "Customer",
        disabled: !!this.props.customer,
        initialValue: this.props.customer?.id,
        selectOptions: fixedIncomeRequest?.customer
          ? convertCustomersToSelectOptions([fixedIncomeRequest?.customer])
          : convertCustomersToSelectOptions(customers),
        changeHandler: this.handleSearchInput,
        selectHandler: this.handleCustomerSelect,
        isSearching,
        colConfig: { span: 24 },
      },
      {
        id: "providerId",
        name: "providerId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Provider",
        label: "Provider",
        selectOptions: providers,
        selectHandler: this.handleProviderSelect,
        initialValue: fixedIncomeRequest?.provider?.id,
        colConfig: { span: 24 },
      },
      {
        id: "productId",
        name: "productId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Product",
        label: "Product",
        selectOptions: products,
        selectHandler: this.handleProductSelect,
        initialValue: fixedIncomeRequest?.product?.id,
        colConfig: { span: 24 },
      },
      {
        id: "assignedToId",
        name: "assignedToId",
        inputType: "search",
        rule: "string",
        required: true,
        placeholder: "Search for user",
        label: "Assigned To",
        selectOptions: users,
        changeHandler: this.handleUserSearchInput,
        selectHandler: this.handleAssignedToSelect,
        isSearching,
        initialValue: fixedIncomeRequest?.assignedTo?.name,
        colConfig: { span: 24 },
      },
      {
        id: "issuedDate",
        name: "issuedDate",
        inputType: "date",
        rule: "string",
        required: false,
        placeholder: "Select issued date",
        label: "Issued Date",
        initialValue: fixedIncomeRequest?.issuedDate,
        colConfig: { span: 24 },
      },
    ];

    return (
      <Fragment>
        <Spin spinning={isLoading}>
          <Row>
            <Col span={12} style={{ margin: "0 20px" }}>
              {updateRequestError && (
                <Alert
                  message="Update Error"
                  description={updateRequestError}
                  type="error"
                  showIcon
                  closable
                  style={{ marginBottom: 16 }}
                  onClose={() =>
                    this.setState({ updateRequestError: undefined })
                  }
                />
              )}
              <BaseForm
                reference={this.reference}
                fieldsToHide={[]}
                layout="vertical"
                fields={fields}
              />
              <Row gutter={16} style={{ marginTop: 24 }}>
                <Col span={11}>
                  <Button
                    onClick={this.toggleCancelModal}
                    style={{ width: "100%" }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col span={13}>
                  <Button
                    type="primary"
                    onClick={this.updateFixedIncomeRequest}
                    loading={isLoading}
                    style={{ width: "100%" }}
                  >
                    Update Fixed Income Request
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          {showCancelModal &&
            ModalCreator({
              title: "Cancel",
              okType: "primary",
              okText: "Yes",
              width: 400,
              showModal: true,
              onOkay: this.cancelRequest,
              toggleModal: this.toggleCancelModal,
              showFooter: true,
              inscription: `Are you sure you want to cancel editing this request?`,
            })}
        </Spin>
      </Fragment>
    );
  }
}

export default withRouter(EditFixedIncomeRequest);
